// Comprehensive SEO metadata for unit conversion categories
// This file provides keyword-rich, search-optimized metadata for each unit conversion category

export const unitConversionSEO = {
  frequency: {
    title: 'Frequency Converter | Hertz to Megahertz, kHz to GHz - Free Online Tool | iconvertify',
    description: 'Convert frequency units instantly: hertz to megahertz, kilohertz to gigahertz, Hz to kHz, MHz to GHz, and more. Free online frequency converter with real-time calculations. Convert between Hz, kHz, MHz, GHz, THz, RPM, BPM, and radians per second. No registration required.',
    keywords: 'frequency converter, hertz to megahertz, kilohertz to gigahertz, Hz to kHz converter, MHz to GHz, frequency unit converter, convert frequency, Hz converter, megahertz converter, gigahertz converter, RPM to Hz, BPM converter, frequency calculator, online frequency converter',
    faq: [
      {
        question: 'How do I convert hertz to megahertz?',
        answer: 'To convert hertz (Hz) to megahertz (MHz), divide the frequency value by 1,000,000. For example, 1,000,000 Hz = 1 MHz. Use our free frequency converter for instant calculations.'
      },
      {
        question: 'What is the difference between hertz and megahertz?',
        answer: 'Hertz (Hz) is the base unit of frequency, while megahertz (MHz) is equal to 1,000,000 hertz. Megahertz is commonly used for radio frequencies and computer processors.'
      },
      {
        question: 'How do I convert RPM to hertz?',
        answer: 'To convert revolutions per minute (RPM) to hertz (Hz), divide the RPM value by 60. For example, 3600 RPM = 60 Hz. Our converter handles this automatically.'
      }
    ],
    popularConversions: [
      'hertz to megahertz',
      'kilohertz to gigahertz',
      'Hz to kHz',
      'MHz to GHz',
      'RPM to Hz',
      'BPM to Hz'
    ]
  },
  length: {
    title: 'Length Converter | Meters to Feet, Inches to CM - Free Online Tool | iconvertify',
    description: 'Convert length units instantly: meters to feet, inches to centimeters, kilometers to miles, and more. Free online length converter supporting metric and imperial units. Convert between meters, feet, inches, centimeters, kilometers, miles, yards, and more. Accurate conversions with step-by-step explanations.',
    keywords: 'length converter, meters to feet, inches to cm, km to miles, feet to meters, cm to inches, length unit converter, convert length, distance converter, metric to imperial, imperial to metric, online length converter, length calculator',
    faq: [
      {
        question: 'How do I convert meters to feet?',
        answer: 'To convert meters to feet, multiply the length in meters by 3.28084. For example, 1 meter = 3.28084 feet. Our converter provides instant, accurate results.'
      },
      {
        question: 'How many centimeters are in an inch?',
        answer: 'There are 2.54 centimeters in one inch. Use our length converter to convert between inches and centimeters instantly.'
      },
      {
        question: 'How do I convert kilometers to miles?',
        answer: 'To convert kilometers to miles, multiply the distance in kilometers by 0.621371. For example, 1 kilometer = 0.621371 miles.'
      }
    ],
    popularConversions: [
      'meters to feet',
      'inches to centimeters',
      'kilometers to miles',
      'feet to meters',
      'cm to inches',
      'miles to km'
    ]
  },
  weight: {
    title: 'Weight Converter | Kilograms to Pounds, Grams to Ounces - Free Online Tool | iconvertify',
    description: 'Convert weight units instantly: kilograms to pounds, grams to ounces, pounds to kg, and more. Free online weight converter supporting metric and imperial units. Convert between kg, lbs, grams, ounces, stones, tons, and more. Accurate mass conversions with real-time calculations.',
    keywords: 'weight converter, kg to lbs, pounds to kilograms, grams to ounces, ounces to grams, weight unit converter, convert weight, mass converter, kg to pounds converter, lbs to kg, online weight converter, weight calculator',
    faq: [
      {
        question: 'How do I convert kilograms to pounds?',
        answer: 'To convert kilograms to pounds, multiply the weight in kg by 2.20462. For example, 1 kilogram = 2.20462 pounds. Our converter provides instant results.'
      },
      {
        question: 'How many grams are in an ounce?',
        answer: 'There are 28.3495 grams in one ounce. Use our weight converter to convert between grams and ounces instantly.'
      },
      {
        question: 'How do I convert pounds to kilograms?',
        answer: 'To convert pounds to kilograms, divide the weight in pounds by 2.20462. For example, 1 pound = 0.453592 kilograms.'
      }
    ],
    popularConversions: [
      'kilograms to pounds',
      'pounds to kilograms',
      'grams to ounces',
      'ounces to grams',
      'kg to lbs',
      'lbs to kg'
    ]
  },
  temperature: {
    title: 'Temperature Converter | Celsius to Fahrenheit, Kelvin Converter - Free Online Tool | iconvertify',
    description: 'Convert temperature units instantly: Celsius to Fahrenheit, Fahrenheit to Celsius, Celsius to Kelvin, and more. Free online temperature converter supporting all major temperature scales. Convert between °C, °F, K (Kelvin), and Rankine. Accurate temperature conversions with formulas explained.',
    keywords: 'temperature converter, celsius to fahrenheit, fahrenheit to celsius, celsius to kelvin, temperature unit converter, convert temperature, °C to °F, °F to °C, kelvin converter, online temperature converter, temperature calculator',
    faq: [
      {
        question: 'How do I convert Celsius to Fahrenheit?',
        answer: 'To convert Celsius to Fahrenheit, multiply the temperature in °C by 9/5 and add 32. Formula: °F = (°C × 9/5) + 32. For example, 0°C = 32°F.'
      },
      {
        question: 'How do I convert Fahrenheit to Celsius?',
        answer: 'To convert Fahrenheit to Celsius, subtract 32 from the temperature in °F and multiply by 5/9. Formula: °C = (°F - 32) × 5/9. For example, 32°F = 0°C.'
      },
      {
        question: 'What is the difference between Celsius and Kelvin?',
        answer: 'Celsius and Kelvin use the same degree size, but Kelvin starts at absolute zero (-273.15°C). To convert: K = °C + 273.15. For example, 0°C = 273.15K.'
      }
    ],
    popularConversions: [
      'celsius to fahrenheit',
      'fahrenheit to celsius',
      'celsius to kelvin',
      'kelvin to celsius',
      '°C to °F',
      '°F to °C'
    ]
  },
  power: {
    title: 'Power Converter | Watts to Horsepower, kW to HP - Free Online Tool | iconvertify',
    description: 'Convert power units instantly: watts to horsepower, kilowatts to HP, watts to kW, and more. Free online power converter for electrical and mechanical power units. Convert between watts, kilowatts, megawatts, horsepower (mechanical and metric), BTU/hour, and more. Accurate power conversions.',
    keywords: 'power converter, watts to horsepower, kW to HP, horsepower to watts, power unit converter, convert power, watts converter, horsepower converter, kilowatts to horsepower, online power converter, power calculator',
    faq: [
      {
        question: 'How do I convert watts to horsepower?',
        answer: 'To convert watts to mechanical horsepower, divide the power in watts by 745.7. For example, 745.7 watts = 1 HP. Our converter handles both mechanical and metric horsepower.'
      },
      {
        question: 'How many watts are in a kilowatt?',
        answer: 'There are 1,000 watts in one kilowatt (kW). Use our power converter to convert between watts and kilowatts instantly.'
      },
      {
        question: 'What is the difference between mechanical and metric horsepower?',
        answer: 'Mechanical horsepower (HP) equals 745.7 watts, while metric horsepower (PS) equals 735.5 watts. Our converter supports both types.'
      }
    ],
    popularConversions: [
      'watts to horsepower',
      'horsepower to watts',
      'kilowatts to horsepower',
      'kW to HP',
      'watts to kilowatts',
      'HP to watts'
    ]
  },
  energy: {
    title: 'Energy Converter | Joules to Calories, kWh Converter - Free Online Tool | iconvertify',
    description: 'Convert energy units instantly: joules to calories, kilowatt-hours to joules, calories to kWh, and more. Free online energy converter for all energy units. Convert between joules, calories, kilocalories, kWh, BTU, electron-volts, foot-pounds, and more. Accurate energy conversions.',
    keywords: 'energy converter, joules to calories, calories to joules, kWh to joules, energy unit converter, convert energy, joules converter, calories converter, kilowatt-hours converter, BTU converter, online energy converter, energy calculator',
    faq: [
      {
        question: 'How do I convert joules to calories?',
        answer: 'To convert joules to calories, divide the energy in joules by 4.184. For example, 4.184 joules = 1 calorie. Our converter provides instant results.'
      },
      {
        question: 'How many joules are in a kilowatt-hour?',
        answer: 'There are 3,600,000 joules in one kilowatt-hour (kWh). Use our energy converter to convert between kWh and joules instantly.'
      },
      {
        question: 'How do I convert calories to kilowatt-hours?',
        answer: 'To convert calories to kWh, first convert calories to joules (multiply by 4.184), then divide by 3,600,000. Our converter handles this automatically.'
      }
    ],
    popularConversions: [
      'joules to calories',
      'calories to joules',
      'kWh to joules',
      'joules to kWh',
      'BTU to joules',
      'calories to kWh'
    ]
  },
  voltage: {
    title: 'Voltage Converter | Volts to Millivolts, kV Converter - Free Online Tool | iconvertify',
    description: 'Convert voltage units instantly: volts to millivolts, kilovolts to volts, microvolts to volts, and more. Free online voltage converter for electrical potential units. Convert between volts, millivolts, microvolts, kilovolts, megavolts, and more. Accurate voltage conversions.',
    keywords: 'voltage converter, volts to millivolts, millivolts to volts, kilovolts to volts, voltage unit converter, convert voltage, volts converter, mV to V, kV to V, online voltage converter, voltage calculator',
    faq: [
      {
        question: 'How do I convert volts to millivolts?',
        answer: 'To convert volts to millivolts, multiply the voltage in volts by 1,000. For example, 1 volt = 1,000 millivolts. Our converter provides instant results.'
      },
      {
        question: 'How many volts are in a kilovolt?',
        answer: 'There are 1,000 volts in one kilovolt (kV). Use our voltage converter to convert between volts and kilovolts instantly.'
      },
      {
        question: 'How do I convert microvolts to volts?',
        answer: 'To convert microvolts to volts, divide the voltage in microvolts by 1,000,000. For example, 1,000,000 microvolts = 1 volt.'
      }
    ],
    popularConversions: [
      'volts to millivolts',
      'millivolts to volts',
      'kilovolts to volts',
      'volts to kilovolts',
      'microvolts to volts',
      'mV to V'
    ]
  },
  resistance: {
    title: 'Resistance Converter | Ohms to Kiloohms, Ω Converter - Free Online Tool | iconvertify',
    description: 'Convert resistance units instantly: ohms to kiloohms, megaohms to ohms, and more. Free online resistance converter for electrical resistance units. Convert between ohms, kiloohms, megaohms, milliohms, and more. Accurate resistance conversions for circuit analysis.',
    keywords: 'resistance converter, ohms to kiloohms, kiloohms to ohms, resistance unit converter, convert resistance, ohms converter, Ω converter, online resistance converter, resistance calculator',
    faq: [
      {
        question: 'How do I convert ohms to kiloohms?',
        answer: 'To convert ohms to kiloohms, divide the resistance in ohms by 1,000. For example, 1,000 ohms = 1 kiloohm. Our converter provides instant results.'
      },
      {
        question: 'How many ohms are in a megaohm?',
        answer: 'There are 1,000,000 ohms in one megaohm (MΩ). Use our resistance converter to convert between ohms and megaohms instantly.'
      },
      {
        question: 'How do I convert milliohms to ohms?',
        answer: 'To convert milliohms to ohms, divide the resistance in milliohms by 1,000. For example, 1,000 milliohms = 1 ohm.'
      }
    ],
    popularConversions: [
      'ohms to kiloohms',
      'kiloohms to ohms',
      'megaohms to ohms',
      'ohms to megaohms',
      'milliohms to ohms',
      'Ω to kΩ'
    ]
  },
  'electric-charge': {
    title: 'Electric Charge Converter | Coulombs to Ampere-Hours - Free Online Tool | iconvertify',
    description: 'Convert electric charge units instantly: coulombs to ampere-hours, millicoulombs to coulombs, and more. Free online electric charge converter. Convert between coulombs, millicoulombs, microcoulombs, nanocoulombs, picocoulombs, ampere-hours, and more. Accurate charge conversions.',
    keywords: 'electric charge converter, coulombs to ampere-hours, coulombs converter, charge unit converter, convert electric charge, C to Ah, ampere-hours converter, online charge converter, charge calculator',
    faq: [
      {
        question: 'How do I convert coulombs to ampere-hours?',
        answer: 'To convert coulombs to ampere-hours, divide the charge in coulombs by 3,600. For example, 3,600 coulombs = 1 ampere-hour. Our converter provides instant results.'
      },
      {
        question: 'How many coulombs are in an ampere-hour?',
        answer: 'There are 3,600 coulombs in one ampere-hour (Ah). Use our electric charge converter to convert between coulombs and ampere-hours instantly.'
      },
      {
        question: 'How do I convert millicoulombs to coulombs?',
        answer: 'To convert millicoulombs to coulombs, divide the charge in millicoulombs by 1,000. For example, 1,000 millicoulombs = 1 coulomb.'
      }
    ],
    popularConversions: [
      'coulombs to ampere-hours',
      'ampere-hours to coulombs',
      'millicoulombs to coulombs',
      'coulombs to millicoulombs',
      'C to Ah',
      'Ah to C'
    ]
  },
  number: {
    title: 'Number System Converter | Binary to Decimal, Hex Converter - Free Online Tool | iconvertify',
    description: 'Convert number systems instantly: binary to decimal, decimal to hex, octal to binary, and more. Free online number system converter. Convert between decimal, binary, octal, hexadecimal, base-36, base-64, Roman numerals, and scientific notation. Accurate number conversions.',
    keywords: 'number converter, binary to decimal, decimal to binary, hex to decimal, decimal to hex, octal converter, binary converter, hexadecimal converter, number system converter, base converter, online number converter',
    faq: [
      {
        question: 'How do I convert binary to decimal?',
        answer: 'To convert binary to decimal, multiply each binary digit by its corresponding power of 2 and sum the results. For example, binary 1010 = (1×2³) + (0×2²) + (1×2¹) + (0×2⁰) = 10 decimal. Our converter handles this automatically.'
      },
      {
        question: 'How do I convert decimal to hexadecimal?',
        answer: 'To convert decimal to hexadecimal, repeatedly divide the number by 16 and use the remainders. For example, 255 decimal = FF hexadecimal. Our converter provides instant results.'
      },
      {
        question: 'How do I convert hexadecimal to binary?',
        answer: 'To convert hex to binary, convert each hex digit to its 4-bit binary equivalent. For example, hex F = binary 1111. Our converter handles this automatically.'
      }
    ],
    popularConversions: [
      'binary to decimal',
      'decimal to binary',
      'hex to decimal',
      'decimal to hex',
      'octal to binary',
      'binary to hex'
    ]
  },
  color: {
    title: 'Color Converter | RGB to HEX, HEX to RGB - Free Online Tool | iconvertify',
    description: 'Convert color formats instantly: RGB to HEX, HEX to RGB, RGB to HSL, CMYK to RGB, and more. Free online color converter supporting all major color models. Convert between RGB, HEX, HSL, HSV, CMYK, and more. Accurate color conversions with visual preview.',
    keywords: 'color converter, RGB to HEX, HEX to RGB, RGB to HSL, HSL to RGB, CMYK to RGB, color code converter, convert color, hex color converter, RGB converter, online color converter, color calculator',
    faq: [
      {
        question: 'How do I convert RGB to HEX?',
        answer: 'To convert RGB to HEX, convert each RGB component (0-255) to its hexadecimal equivalent (00-FF). For example, RGB(255, 0, 0) = #FF0000. Our converter handles this automatically.'
      },
      {
        question: 'How do I convert HEX to RGB?',
        answer: 'To convert HEX to RGB, split the hex code into pairs and convert each pair to decimal. For example, #FF0000 = RGB(255, 0, 0). Our converter provides instant results.'
      },
      {
        question: 'How do I convert RGB to HSL?',
        answer: 'RGB to HSL conversion involves calculating hue, saturation, and lightness from RGB values. Our color converter handles this complex calculation automatically.'
      }
    ],
    popularConversions: [
      'RGB to HEX',
      'HEX to RGB',
      'RGB to HSL',
      'HSL to RGB',
      'CMYK to RGB',
      'RGB to CMYK'
    ]
  },
  image: {
    title: 'Image Format Converter | JPG to PNG, WebP Converter - Free Online Tool | iconvertify',
    description: 'Convert image formats instantly: JPG to PNG, PNG to JPG, WebP to PNG, and more. Free online image format converter. Convert between JPG, PNG, GIF, WebP, BMP, TIFF, and more. High-quality image conversions with format optimization.',
    keywords: 'image converter, JPG to PNG, PNG to JPG, WebP to PNG, image format converter, convert image, JPG converter, PNG converter, online image converter, image format converter',
    faq: [
      {
        question: 'How do I convert JPG to PNG?',
        answer: 'To convert JPG to PNG, upload your JPG image and select PNG as the output format. Our converter maintains image quality while changing the format. PNG supports transparency, unlike JPG.'
      },
      {
        question: 'What is the difference between JPG and PNG?',
        answer: 'JPG is a lossy format ideal for photographs, while PNG is a lossless format that supports transparency and is better for graphics with text or sharp edges.'
      },
      {
        question: 'How do I convert WebP to PNG?',
        answer: 'Upload your WebP image and select PNG as the output format. Our converter handles the conversion while preserving image quality.'
      }
    ],
    popularConversions: [
      'JPG to PNG',
      'PNG to JPG',
      'WebP to PNG',
      'PNG to WebP',
      'JPG to WebP',
      'WebP to JPG'
    ]
  }
};

// Get SEO metadata for a specific category
export const getUnitConversionSEO = (category) => {
  // Handle both 'electric-charge' and 'electricCharge' formats
  const categoryKey = category === 'electricCharge' ? 'electric-charge' : category;
  
  return unitConversionSEO[categoryKey] || {
    title: `${category} Converter | Free Online Unit Converter - iconvertify`,
    description: `Convert ${category} units online for free. Fast, accurate, and reliable unit conversion. No registration required.`,
    keywords: `${category}, ${category} converter, unit converter, online converter, free converter`,
    faq: [],
    popularConversions: []
  };
};

// Generate FAQ structured data
export const generateFAQStructuredData = (faq) => {
  if (!faq || faq.length === 0) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
};

// Generate HowTo structured data for unit conversion
export const generateHowToStructuredData = (category, categoryTitle) => {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": `How to Convert ${categoryTitle} Units`,
    "description": `Learn how to convert ${categoryTitle.toLowerCase()} units using our free online converter.`,
    "step": [
      {
        "@type": "HowToStep",
        "name": "Select Units",
        "text": "Choose the unit you want to convert from and the unit you want to convert to.",
        "position": 1
      },
      {
        "@type": "HowToStep",
        "name": "Enter Value",
        "text": "Enter the value you want to convert in the input field.",
        "position": 2
      },
      {
        "@type": "HowToStep",
        "name": "Get Result",
        "text": "The converted value will be displayed instantly with step-by-step explanations.",
        "position": 3
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": `${categoryTitle} Converter - iconvertify`
      }
    ]
  };
};

